import React from 'react';
import { motion } from 'framer-motion';
import { ZapIcon, TargetIcon, MapIcon, SparklesIcon } from 'lucide-react';
interface QuizIntroProps {
  onStart: () => void;
}
export function QuizIntro({
  onStart
}: QuizIntroProps) {
  return <motion.div className="min-h-screen w-full flex items-center justify-center px-4 py-12" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0,
    x: -60
  }} transition={{
    duration: 0.4
  }} data-id="element-120">
      <div className="max-w-2xl w-full text-center" data-id="element-121">
        {/* Badge */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1,
        duration: 0.5
      }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-8" data-id="element-122">
          <SparklesIcon className="w-4 h-4 text-cyan-400" data-id="element-123" />
          <span className="text-sm text-cyan-300 font-medium" data-id="element-124">
            Test de 2 minutos
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1 initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2,
        duration: 0.6
      }} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6" data-id="element-125">
          Descubre tu{' '}
          <span className="text-transparent bg-clip-text" style={{
          backgroundImage: 'linear-gradient(135deg, #00d4ff, #0ea5e9, #00d4ff)'
        }} data-id="element-126">
            "Ruta de Cash"
          </span>{' '}
          con IA en 48H
          <br data-id="element-127" />
          <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white/70" data-id="element-128">
            (según tu perfil)
          </span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.35,
        duration: 0.5
      }} className="text-lg sm:text-xl text-white/60 max-w-xl mx-auto mb-10 leading-relaxed" data-id="element-129">
          Responde este test y te diré cuál es el camino más rápido para ti:{' '}
          <strong className="text-white/80" data-id="element-130">
            servicios con IA, automatizaciones, contenido que vende o productos
            digitales.
          </strong>
        </motion.p>

        {/* Bullets */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5,
        duration: 0.5
      }} className="flex flex-col gap-4 max-w-md mx-auto mb-12 text-left" data-id="element-131">
          <div className="flex items-start gap-3" data-id="element-132">
            <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center" data-id="element-133">
              <TargetIcon className="w-4 h-4 text-cyan-400" data-id="element-134" />
            </div>
            <p className="text-white/70 text-base" data-id="element-135">
              Identifica{' '}
              <strong className="text-white" data-id="element-136">qué te está frenando</strong>{' '}
              (tiempo, claridad, oferta, tráfico)
            </p>
          </div>
          <div className="flex items-start gap-3" data-id="element-137">
            <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center" data-id="element-138">
              <MapIcon className="w-4 h-4 text-cyan-400" data-id="element-139" />
            </div>
            <p className="text-white/70 text-base" data-id="element-140">
              Te doy tu <strong className="text-white" data-id="element-141">plan recomendado</strong>{' '}
              en base a tus respuestas
            </p>
          </div>
          <div className="flex items-start gap-3" data-id="element-142">
            <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center" data-id="element-143">
              <ZapIcon className="w-4 h-4 text-cyan-400" data-id="element-144" />
            </div>
            <p className="text-white/70 text-base" data-id="element-145">
              Sin tecnicismos. En español.{' '}
              <strong className="text-white" data-id="element-146">Paso a paso.</strong>
            </p>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.65,
        duration: 0.4,
        type: 'spring',
        stiffness: 200
      }} data-id="element-147">
          <button onClick={onStart} className="group relative inline-flex items-center gap-2 px-10 py-4 rounded-xl text-lg font-bold text-black transition-all duration-200 hover:scale-105 active:scale-100" style={{
          background: 'linear-gradient(135deg, #f59e0b, #ff6b2b)',
          boxShadow: '0 0 30px rgba(245, 158, 11, 0.3), 0 4px 20px rgba(255, 107, 43, 0.2)'
        }} aria-label="Empezar Test Gratis" data-id="element-148">
            Empezar Test Gratis
            <ZapIcon className="w-5 h-5 transition-transform group-hover:translate-x-0.5" data-id="element-149" />
          </button>
        </motion.div>

        {/* Microdisclaimer */}
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.9,
        duration: 0.5
      }} className="mt-6 text-xs text-white/30" data-id="element-150">
          Resultados orientativos. Depende de tu ejecución.
        </motion.p>
      </div>
    </motion.div>;
}